import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  IonicModule,
  ModalController,
  Platform,
  ToastController
} from '@ionic/angular';
import { KeycloakService } from 'keycloak-angular';
import { Observable, Subscription } from 'rxjs';
import { forsideRoute, nyttigRoute } from '../../../routes';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { HttpClient } from '@angular/common/http';
import { Clipboard } from '@capacitor/clipboard';
import { Article } from 'src/app/services/sanity/sanity.types';
import { SanityService } from 'src/app/services/sanity/sanity.service';
import { PortableTextBlock } from '@portabletext/types';
import { toHTML } from '@portabletext/to-html';
import { contentBlockComponents } from 'src/app/services/sanity/sanity.utils';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'article',
  templateUrl: './article.page.html',
  styleUrls: ['./article.page.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule, IonicModule]
})
export class ArticlePage implements OnInit, OnDestroy {
  blnLoggedIn: boolean;
  loggedIn: boolean;
  userInfo: Promise<Keycloak.KeycloakProfile>;

  natParams: Params;
  article: Article;
  articleContent: SafeHtml;

  rabattResponse: Observable<Rabattkode>;
  rabattkode: string;
  isApp: boolean;
  querySubscription: Subscription;
  rabattSubscription: Subscription;

  pageParam: string;
  senderParam: string;

  constructor(
    private _keycloakService: KeycloakService,
    private route: ActivatedRoute,
    private _sanitizer: DomSanitizer,
    private http: HttpClient,
    private analyticsService: AnalyticsService,
    private _toast: ToastController,
    private modalCtrl: ModalController,
    public platform: Platform,
    private sanityService: SanityService,
    private titleService: Title,
    private router: Router
  ) {}

  ngOnDestroy(): void {
    this.querySubscription.unsubscribe();
    this.rabattSubscription?.unsubscribe();
  }

  ngOnInit() {
    this.rabattkode = undefined;
    this.isApp = this.platform.is('capacitor');

    this.querySubscription = this.route.queryParams.subscribe((params) => {
      this.natParams = params;
      if (params['sender']) {
        this.senderParam = params['sender'];
      }
      if (params['page']) {
        this.pageParam = params['page'];
      }

      const navState = this.router.getCurrentNavigation().extras?.state;
      if (navState) {
        if (navState.page) {
          this.pageParam = navState.page;
        }
        if (navState.sender) {
          this.senderParam = navState.sender;
        }
      }

      if (this.pageParam) {
        this.sanityService.getArticleBySlug(this.pageParam).then((res) => {
          this.article = res;
          this.articleContent = this.contentToHtml(this.article.content);
          this.titleService.setTitle(this.article.title + ' – Ishavskraft');
        });
      }
    });
  }

  ionViewDidEnter() {
    this.loggedIn = this._keycloakService.isLoggedIn();
    if (this.loggedIn) {
      this.userInfo = this._keycloakService.loadUserProfile(true).catch();
    }
  }

  contentToHtml(content: PortableTextBlock[]): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(
      toHTML(content, {
        components: contentBlockComponents(
          this._sanitizer,
          this.platform.width() > 700
        )
      })
    );
  }

  navBack() {
    if (this.senderParam) {
      if (this.senderParam == 'forside') {
        this.router.navigate([forsideRoute]);
      } else {
        this.router.navigate([nyttigRoute]);
      }
    } else {
      this.router.navigate([nyttigRoute]);
    }
  }

  logClick(artikkel: Article) {
    const page = this.pageParam;
    const toUrl = artikkel.link_article.url;

    this.analyticsService.logEvent_selectContent(
      'artikkel klikk les mer: ' + page,
      toUrl
    );
  }

  cancel() {
    this.modalCtrl.dismiss();
  }

  getRabattkode(article: Article) {
    // Nullstill mellom artikler
    this.rabattkode = undefined;
    const rabattkey = article.discount_key || '';
    // Endepunktet henter koder herfra: https://workflow-automation.podio.com/podiofeed.php?c=6618&a=567036&f=6032
    // Pga CORS problemer med å hente direkte fra Podio går vi via en proxy i apiet:
    const link = `https://apiprivat.ishavskraft.no/bruker/kode/${rabattkey}`;
    this.rabattResponse = this.http.get<Rabattkode>(link);
    this.rabattSubscription = this.rabattResponse.subscribe(
      (result: Rabattkode) => {
        this.rabattkode = result.kode.kode;
      }
    );
    this.analyticsService.logEvent_selectContent(
      `${rabattkey} rabattkode åpnet`
    );
  }

  copyToClipboard(text: string, supplier: string) {
    if (text == null || text.length == 0) {
      return;
    }
    const confirmCopy = () => {
      this._toast
        .create({
          message: 'Rabattkode kopiert',
          duration: 2500,
          position: 'middle',
          color: 'success'
        })
        .then((toast) => {
          toast.present();
          this.analyticsService.logEvent_selectContent(
            `${supplier} rabattkode kopiert`
          );
        });
    };
    if (this.isApp) {
      Clipboard.write({ string: text }).then(confirmCopy);
    } else {
      navigator.clipboard.writeText(text).then(confirmCopy);
    }
  }
}

interface Rabattkode {
  kode: {
    kode: string;
    // Endepunktet har allerede validert gyldighetsperioden
    validFrom: string;
    validTo: string;
  };
}
